import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Tx } from './EditMode';
import {
  LogOut, LayoutDashboard, LogIn, Home, Info, Newspaper, Menu, X,
} from 'lucide-react';

export const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll while menu is open on mobile
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  const go = (path) => { setMenuOpen(false); navigate(path); };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-lg bg-white/90">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
        <div className="flex justify-between items-center gap-2 sm:gap-3">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo"
                className="flex items-center gap-2 text-lg sm:text-2xl font-bold text-slate-900 font-['Outfit'] shrink-0">
            <Home className="w-5 h-5 text-blue-600" />
            <span className="hidden lg:inline">Flying with the Bowen Steinslettens</span>
            <span className="hidden sm:inline lg:hidden">Bowen Steinslettens</span>
            <span className="sm:hidden">Home</span>
          </Link>

          {/* Desktop / tablet nav (≥ sm) */}
          <div className="hidden sm:flex items-center gap-1 sm:gap-2 md:gap-3">
            <button
              data-testid="nav-mission-btn"
              onClick={() => navigate('/mission')}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-slate-700 hover:text-blue-600 font-medium text-sm transition-colors duration-200"
              title={t('quicklinks.about.title')}
            >
              <Info className="w-4 h-4 shrink-0" />
              <span><Tx k="quicklinks.about.title" /></span>
            </button>
            <button
              data-testid="nav-journey-btn"
              onClick={() => navigate('/journey')}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-slate-700 hover:text-blue-600 font-medium text-sm transition-colors duration-200"
              title={t('home.journey.title')}
            >
              <Newspaper className="w-4 h-4 shrink-0" />
              <span><Tx k="home.journey.title" /></span>
            </button>
            {user && user.role === 'supporter' && (
              <button data-testid="nav-account-btn" onClick={() => navigate('/account')}
                      className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-slate-700 hover:text-blue-600 font-medium text-sm">
                <LayoutDashboard className="w-4 h-4" />
                <span><Tx k="nav.account" /></span>
              </button>
            )}
            {user && user.role === 'admin' && (
              <button data-testid="nav-admin-dashboard-btn" onClick={() => navigate('/admin/dashboard')}
                      className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-slate-700 hover:text-blue-600 font-medium text-sm">
                <LayoutDashboard className="w-4 h-4" />
                <span><Tx k="nav.admin" /></span>
              </button>
            )}
            <LanguageSwitcher variant="inline" />
            {!user && (
              <button data-testid="nav-login-btn" onClick={() => navigate('/login')}
                      className="flex items-center gap-1.5 px-4 sm:px-5 py-2 bg-blue-600 text-white rounded-full font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md">
                <LogIn className="w-4 h-4" />
                <span><Tx k="nav.login" /></span>
              </button>
            )}
            {user && (
              <button data-testid="nav-logout-btn" onClick={handleLogout}
                      className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-slate-700 hover:text-red-600 font-medium text-sm">
                <LogOut className="w-4 h-4" />
                <span><Tx k="nav.logout" /></span>
              </button>
            )}
          </div>

          {/* Mobile controls (< sm) */}
          <div className="flex sm:hidden items-center gap-1">
            <LanguageSwitcher variant="inline" />
            <button
              data-testid="nav-mobile-menu-toggle"
              onClick={() => setMenuOpen(!menuOpen)}
              className={`ml-1 inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                menuOpen ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile slide-down panel */}
      {menuOpen && (
        <>
          {/* Backdrop covers the rest of the page while menu is open */}
          <div
            data-testid="nav-mobile-backdrop"
            onClick={() => setMenuOpen(false)}
            className="sm:hidden fixed inset-0 top-[64px] bg-slate-900/40 backdrop-blur-sm z-40"
          />
          <div
            data-testid="nav-mobile-menu"
            className="sm:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-200 shadow-xl z-50 animate-slide-down"
          >
            <div className="p-3 space-y-1">
              <MenuItem testid="nav-mobile-mission-btn" icon={Info}
                        onClick={() => go('/mission')} label={<Tx k="quicklinks.about.title" />} />
              <MenuItem testid="nav-mobile-journey-btn" icon={Newspaper}
                        onClick={() => go('/journey')} label={<Tx k="home.journey.title" />} />
              {user && user.role === 'supporter' && (
                <MenuItem testid="nav-mobile-account-btn" icon={LayoutDashboard}
                          onClick={() => go('/account')} label={<Tx k="nav.account" />} />
              )}
              {user && user.role === 'admin' && (
                <MenuItem testid="nav-mobile-admin-btn" icon={LayoutDashboard}
                          onClick={() => go('/admin/dashboard')} label={<Tx k="nav.admin" />} />
              )}

              <div className="pt-2 mt-2 border-t border-slate-100">
                {!user && (
                  <button
                    data-testid="nav-mobile-login-btn"
                    onClick={() => go('/login')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors"
                  >
                    <LogIn className="w-5 h-5" />
                    <Tx k="nav.login" />
                  </button>
                )}
                {user && (
                  <button
                    data-testid="nav-mobile-logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-rose-600 hover:bg-rose-50 rounded-xl font-semibold"
                  >
                    <LogOut className="w-5 h-5" />
                    <Tx k="nav.logout" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-down { animation: slide-down 0.2s ease-out both; }
      `}</style>
    </nav>
  );
};

const MenuItem = ({ testid, icon: Icon, onClick, label }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-xl font-medium transition-colors"
  >
    <Icon className="w-5 h-5 text-blue-600 shrink-0" />
    <span className="text-base">{label}</span>
  </button>
);
